import React from "react";
import { useTranslation } from "react-i18next";
import {
  ChartNoAxesCombined,
  ClipboardCheck,
  Building2,
  ArchiveRestore,
  CloudCog,
  FileCheck2,
  Funnel,
  Handshake,
  Landmark,
  ShieldCheck,
  ServerCog,
  Workflow,
  Zap,
} from "lucide-react";
import Button from "./Button";

const iconByName = {
  standardize: Funnel,
  control: Workflow,
  insight: ChartNoAxesCombined,
  pdf: ShieldCheck,
  integration: FileCheck2,
  value: ClipboardCheck,
  finance: Landmark,
  public: Building2,
  enterprise: Zap,
  migration: ArchiveRestore,
  modernization: CloudCog,
  archivePlatform: ServerCog,
};

const cardToneClasses = {
  light: {
    card: "bg-white text-[#1b3048]",
    icon: "!text-[#043f53]",
    heading: "text-[#1b3048]",
    body: "text-[#3e4e62]",
  },
  warm: {
    card: "bg-gray text-[#1b3048]",
    icon: "!text-[#8a6a53]",
    heading: "text-[#2e3848]",
    body: "text-[#45556d]",
  },
  slate: {
    card: "bg-secondary text-white",
    icon: "!text-[#fff1e8]",
    heading: "text-white",
    body: "text-[#f7f7f7]",
  },
};

/**
 * A reusable full-width feature section. Pass translation key prefixes through
 * `items` to keep its copy and presentation independent of a particular page.
 */
function FeatureCards({ title, items, cta }) {
  const { t } = useTranslation();
  const CardHeading = title ? "h3" : "h2";

  return (
    <section
      className="!bg-main px-5 py-16 text-white md:px-6 md:py-24 lg:py-32"
      aria-labelledby={title ? "feature-cards-title" : undefined}
    >
      <div className="mx-auto max-w-2xl">
        {title && (
          <h2
            id="feature-cards-title"
            className="mb-8 max-w-xl text-3xl font-bold leading-tight tracking-[-0.03em] text-white md:mb-12 md:text-4xl"
          >
            {t(title)}
          </h2>
        )}
        <div className="grid gap-5 md:grid-cols-3 md:gap-6">
          {items.map(({ icon, illustration, key, tone = "light", list = false, listStyle = "prominent", duotone = false }) => {
            const Icon = iconByName[icon];
            const toneClasses = cardToneClasses[tone] || cardToneClasses.light;
            const description = t(`${key}.description`);
            const points = t(`${key}.points`, { returnObjects: true });
            const hasPoints = Array.isArray(points);

            return (
              <div
                className={`relative flex min-h-0 flex-col justify-center rounded-2xl p-7 shadow-[0_18px_40px_-28px_rgba(4,63,83,0.85)] md:min-h-[27rem] md:p-9 lg:p-11 ${toneClasses.card}`}
                key={key}
              >
                <div className="mb-4 flex justify-center md:mb-5">
                  {illustration ? (
                    <img src={illustration} alt="" aria-hidden="true" width="160" height="128" className="h-24 w-[7.5rem] md:h-28 md:w-[8.75rem]" />
                  ) : Icon && (duotone ? (
                    <span className="relative block h-16 w-16 lg:h-[4.75rem] lg:w-[4.75rem]" aria-hidden="true">
                      <span className="absolute bottom-0 right-0 h-10 w-10 rounded-full bg-[#d2a789]" />
                      <span className="absolute left-0 top-0 h-12 w-12 rotate-6 rounded-[1.1rem] bg-white/65" />
                      <Icon
                        className="absolute inset-[0.35rem] h-[calc(100%-0.7rem)] w-[calc(100%-0.7rem)] text-[#34465f]"
                        strokeWidth={1.7}
                      />
                    </span>
                  ) : (
                    <Icon
                      className={`!h-12 !w-12 md:!h-14 md:!w-14 lg:!h-[4.25rem] lg:!w-[4.25rem] ${toneClasses.icon}`}
                      aria-hidden="true"
                      strokeWidth={1.75}
                    />
                  ))}
                </div>
                <CardHeading
                  className={`mb-4 whitespace-pre-line text-center text-[clamp(1.45rem,2.2vw,2rem)] font-bold leading-[1.15] tracking-[-0.025em] ${toneClasses.heading}`}
                >
                  {t(`${key}.title`)}
                </CardHeading>
                {list ? (
                  <div className="max-w-[36ch] self-center">
                    {hasPoints && (
                      <p className={`mb-5 text-center text-base leading-[1.6] ${toneClasses.body}`}>
                        {description}
                      </p>
                    )}
                    <ul className={`mb-0 list-disc space-y-2 pl-5 text-left leading-[1.5] ${listStyle === "standard" ? "text-base font-normal" : "text-[1.0625rem] font-medium"} ${toneClasses.body}`}>
                      {(hasPoints ? points : description.split("\n")).map((item) => (
                        <li key={item}>{item.replace(/^[–-]\s*/, "")}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p
                    className={`mb-0 max-w-[36ch] self-center whitespace-pre-line text-center text-base leading-[1.6] ${toneClasses.body}`}
                  >
                    {description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {cta && (
          <div className="mx-auto mt-10 flex w-full max-w-3xl flex-col gap-5 border-t border-white/30 pt-8 text-center md:flex-row md:items-center md:text-left">
            <Handshake
              className="mx-auto h-10 w-10 shrink-0 text-[#fff1e8] md:mx-0"
              aria-hidden="true"
              strokeWidth={1.75}
            />
            <div className="max-w-[32rem] md:mr-auto">
              <h3 className="mb-1 text-2xl font-bold leading-tight tracking-[-0.025em] text-white">
                {t(cta.title)}
              </h3>
              <p className="mb-0 text-base leading-relaxed text-white/85">
                {t(cta.description)}
              </p>
            </div>
            <Button color="white" size="large" onClick={cta.onClick}>
              {t(cta.label)}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

export default FeatureCards;
